// 신청자 목록 드로어
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Box,
  Text,
  Stack,
  Button,
  Badge,
  Flex,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Divider,
  Grid
} from '@chakra-ui/react';
import { ViewIcon, CheckIcon, CloseIcon, CopyIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { useAuth } from 'contexts/AuthContext';
import {
  getScheduleApplications,
  getUserProfile,
  selectApplicant,
  rejectApplicant,
  cancelSelectedApplicant,
  getActiveAcademicSchedule,
  updateWorkSchedule,
  getUserSemesterWorkers,
  deleteWorkSchedule
} from 'utils/firestoreUtils';
import { 
  formatScheduleDateTime, 
  getDayOfWeek,
  getDayNameKorean 
} from 'utils/scheduleUtils';
import WorkerAssignmentModal from './WorkerAssignmentModal';

function ApplicationList({ isOpen, onClose, schedule, onUpdate, onCopy }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applicantsData, setApplicantsData] = useState({});
  const [workerData, setWorkerData] = useState({});
  
  const { 
    isOpen: isScheduleModalOpen, 
    onOpen: onScheduleModalOpen, 
    onClose: onScheduleModalClose 
  } = useDisclosure();
  const { 
    isOpen: isWorkerAssignOpen, 
    onOpen: onWorkerAssignOpen, 
    onClose: onWorkerAssignClose 
  } = useDisclosure();
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  useEffect(() => {
    if (isOpen && schedule) {
      loadApplications();
    }
  }, [isOpen, schedule]);

  const loadApplications = async () => {
    if (!schedule) return;

    try {
      setLoading(true);
      const apps = await getScheduleApplications(schedule.id);
      setApplications(apps);

      // 각 신청자의 프로필 정보 및 근무자 정보 가져오기
      const profilesData = {};
      const workersData = {};
      
      for (const app of apps) {
        const profile = await getUserProfile(app.userId);
        if (profile) {
          profilesData[app.userId] = profile;
        }
        
        // 근무자 정보 가져오기
        const workers = await getUserSemesterWorkers(app.userId);
        if (workers.length > 0) {
          // 가장 최근 학기의 근무자 정보 사용
          workersData[app.userId] = workers[0];
        }
      }
      
      setApplicantsData(profilesData);
      setWorkerData(workersData);
    } catch (error) {
      console.error('신청 목록 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '신청 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSchedule = async (userId) => {
    try {
      const academicSchedule = await getActiveAcademicSchedule(userId);
      setSelectedApplicant({
        userId,
        profile: applicantsData[userId],
        academicSchedule
      });
      onScheduleModalOpen();
    } catch (error) {
      console.error('학업시간표 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '학업시간표를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    }
  };

  const handleSelect = async (application) => {
    if (!window.confirm(`${applicantsData[application.userId]?.name}님을 선정하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      await selectApplicant(
        application.id,
        application.scheduleId,
        application.userId,
        currentUser.uid
      );
      
      toast({
        title: '선정 완료',
        description: '신청자가 선정되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      await loadApplications();
      onUpdate();
    } catch (error) {
      console.error('선정 오류:', error);
      toast({
        title: '선정 실패',
        description: error.message || '신청자 선정에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (application) => {
    const reason = window.prompt('거절 사유를 입력하세요 (선택사항)');
    if (reason === null) return; // 취소 버튼

    try {
      setLoading(true);
      await rejectApplicant(application.id, reason);
      
      toast({
        title: '거절 완료',
        description: '신청이 거절되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      await loadApplications();
      onUpdate();
    } catch (error) {
      console.error('거절 오류:', error);
      toast({
        title: '거절 실패',
        description: '신청 거절에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSelected = async (application) => {
    if (!window.confirm(`${applicantsData[application.userId]?.name}님의 배정을 취소하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      await cancelSelectedApplicant(application.id, application.scheduleId);
      
      toast({
        title: '배정 취소 완료',
        description: '배정이 취소되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      await loadApplications();
      onUpdate();
    } catch (error) {
      console.error('배정 취소 오류:', error);
      toast({
        title: '배정 취소 실패',
        description: '배정 취소에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSchedule = async () => {
    if (!schedule) return;
    
    if (!window.confirm('스케줄을 마감하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      await updateWorkSchedule(schedule.id, { 
        status: 'closed',
        confirmedAt: new Date()
      });
      
      toast({
        title: '마감 완료',
        description: '스케줄이 마감되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('마감 오류:', error);
      toast({
        title: '마감 실패',
        description: '스케줄 마감에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReopenSchedule = async () => {
    if (!schedule) return;
    
    if (!window.confirm('마감을 취소하고 모집중으로 전환하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      await updateWorkSchedule(schedule.id, { 
        status: 'open',
        confirmedAt: null
      });
      
      toast({
        title: '마감 취소 완료',
        description: '스케줄이 다시 모집중으로 전환되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('마감 취소 오류:', error);
      toast({
        title: '마감 취소 실패',
        description: '마감 취소에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!schedule) return;
    
    // 신청자가 있는 경우 경고
    if (applications.length > 0) {
      const hasSelected = applications.some(app => app.status === 'selected');
      if (hasSelected) {
        if (!window.confirm('⚠️ 이미 선정된 신청자가 있습니다.\n정말로 이 스케줄을 삭제하시겠습니까?\n(선정된 신청자 정보도 함께 삭제됩니다)')) {
          return;
        }
      } else {
        if (!window.confirm(`⚠️ ${applications.length}명의 신청자가 있습니다.\n정말로 이 스케줄을 삭제하시겠습니까?`)) {
          return;
        }
      }
    } else {
      if (!window.confirm('이 스케줄을 삭제하시겠습니까?')) {
        return;
      }
    }

    try {
      setLoading(true);
      await deleteWorkSchedule(schedule.id);
      
      toast({
        title: '삭제 완료',
        description: '스케줄이 삭제되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('삭제 오류:', error);
      toast({
        title: '삭제 실패',
        description: '스케줄 삭제에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  if (!schedule) return null;

  const pendingApps = applications.filter(app => app.status === 'pending');
  const selectedApps = applications.filter(app => app.status === 'selected');

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>스케줄 상세 및 신청자 목록</DrawerHeader>

          <DrawerBody>
            <Stack spacing={4}>
              {/* 스케줄 정보 */}
              <Box p={4} borderWidth="1px" borderRadius="lg">
                <Flex justify="space-between" align="start" mb={2}>
                  <Box flex={1}>
                    <Text fontSize="lg" fontWeight="bold" mb={1}>
                      {schedule.location}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatScheduleDateTime(schedule.workDate, schedule.startTime, schedule.endTime)}
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    leftIcon={<CopyIcon />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={() => {
                      onCopy(schedule);
                      onClose();
                    }}
                  >
                    복사
                  </Button>
                </Flex>
                <Flex gap={2} mt={2} flexWrap="wrap">
                  <Badge colorScheme={
                    schedule.status === 'open' ? 'blue' : 
                    schedule.status === 'closed' ? 'green' : 'gray'
                  }>
                    {schedule.status === 'open' ? '모집중' : 
                     schedule.status === 'closed' ? '마감' : '완료'}
                  </Badge>
                  <Badge colorScheme="purple">
                    {schedule.confirmedCount || 0} / {schedule.requiredHeadcount}명
                  </Badge>
                  <Badge colorScheme="orange">시급 {schedule.wageAmount?.toLocaleString()}원</Badge>
                </Flex>
                {schedule.description && (
                  <Text fontSize="sm" mt={2} color="gray.700">
                    {schedule.description}
                  </Text>
                )}
              </Box>

              <Divider />

              {/* 선정된 신청자 */}
              {selectedApps.length > 0 && (
                <>
                  <Text fontSize="md" fontWeight="bold" mb={3}>
                    선정된 신청자 ({selectedApps.length}명)
                  </Text>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>이름</Th>
                        <Th>연락처</Th>
                        <Th>선정일시</Th>
                        <Th>잔여 시간</Th>
                        <Th>작업</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedApps.map((app) => {
                        const profile = applicantsData[app.userId];
                        const worker = workerData[app.userId];
                        return (
                          <Tr key={app.id}>
                            <Td>{profile?.name || '-'}</Td>
                            <Td fontSize="sm">{profile?.phone || '-'}</Td>
                            <Td fontSize="xs">
                              {app.selectedAt ? new Date(app.selectedAt.seconds * 1000).toLocaleString('ko-KR') : '-'}
                            </Td>
                            <Td>
                              {worker ? (
                                <Flex direction="column" fontSize="xs">
                                  <Text fontWeight="bold" color="blue.600">
                                    잔여: {worker.remainingHours}시간
                                  </Text>
                                  <Text color="gray.600">
                                    신청: {worker.appliedHours}시간 / 최대: {worker.maxWorkHours}시간
                                  </Text>
                                </Flex>
                              ) : (
                                <Text fontSize="xs" color="gray.400">정보 없음</Text>
                              )}
                            </Td>
                            <Td>
                              <Flex gap={1}>
                                <IconButton
                                  icon={<ViewIcon />}
                                  size="xs"
                                  onClick={() => handleViewSchedule(app.userId)}
                                  aria-label="시간표 확인"
                                  title="학업시간표 확인"
                                />
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="xs"
                                  colorScheme="red"
                                  onClick={() => handleCancelSelected(app)}
                                  isLoading={loading}
                                  aria-label="배정 취소"
                                  title="배정 취소"
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                  <Divider />
                </>
              )}

              {/* 대기중 신청자 */}
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="md" fontWeight="bold">
                  대기중 신청자 ({pendingApps.length}명)
                </Text>
                {schedule.status !== 'closed' && schedule.status !== 'completed' && (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<AddIcon />}
                    onClick={onWorkerAssignOpen}
                  >
                    근무자 할당
                  </Button>
                )}
              </Flex>

              {pendingApps.length === 0 ? (
                <Box textAlign="center" py={4} bg="gray.50" borderRadius="md">
                  <Text color="gray.500">
                    대기중인 신청이 없습니다.
                  </Text>
                  <Text fontSize="sm" color="gray.400" mt={1}>
                    위의 "근무자 할당" 버튼으로 직접 배정할 수 있습니다.
                  </Text>
                </Box>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>이름</Th>
                      <Th>연락처</Th>
                      <Th>신청일시</Th>
                      <Th>잔여 시간</Th>
                      <Th>작업</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pendingApps.map((app) => {
                      const profile = applicantsData[app.userId];
                      const worker = workerData[app.userId];
                      return (
                        <Tr key={app.id}>
                          <Td>{profile?.name || '-'}</Td>
                          <Td fontSize="sm">{profile?.phone || '-'}</Td>
                          <Td fontSize="xs">
                            {app.appliedAt ? new Date(app.appliedAt.seconds * 1000).toLocaleString('ko-KR') : '-'}
                          </Td>
                          <Td>
                            {worker ? (
                              <Flex direction="column" fontSize="xs">
                                <Text fontWeight="bold" color="blue.600">
                                  잔여: {worker.remainingHours}시간
                                </Text>
                                <Text color="gray.600">
                                  신청: {worker.appliedHours}시간 / 최대: {worker.maxWorkHours}시간
                                </Text>
                              </Flex>
                            ) : (
                              <Text fontSize="xs" color="gray.400">정보 없음</Text>
                            )}
                          </Td>
                          <Td>
                            <Flex gap={1}>
                              <IconButton
                                icon={<ViewIcon />}
                                size="xs"
                                onClick={() => handleViewSchedule(app.userId)}
                                aria-label="시간표 확인"
                                title="학업시간표 확인"
                              />
                              <IconButton
                                icon={<CheckIcon />}
                                size="xs"
                                colorScheme="green"
                                onClick={() => handleSelect(app)}
                                isLoading={loading}
                                aria-label="선정"
                                title="선정"
                              />
                              <IconButton
                                icon={<CloseIcon />}
                                size="xs"
                                colorScheme="red"
                                onClick={() => handleReject(app)}
                                isLoading={loading}
                                aria-label="거절"
                                title="거절"
                              />
                            </Flex>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              )}
            </Stack>
          </DrawerBody>

          <DrawerFooter>
            <Flex justify="space-between" width="100%">
              <Button
                leftIcon={<DeleteIcon />}
                colorScheme="red"
                variant="outline"
                onClick={handleDeleteSchedule}
                isLoading={loading}
              >
                스케줄 삭제
              </Button>
              
              <Flex gap={3}>
                {schedule.status === 'open' ? (
                  <Button
                    colorScheme="orange"
                    onClick={handleCloseSchedule}
                    isLoading={loading}
                  >
                    스케줄 마감
                  </Button>
                ) : schedule.status === 'closed' && (
                  <Button
                    colorScheme="blue"
                    onClick={handleReopenSchedule}
                    isLoading={loading}
                  >
                    마감 취소 (모집중으로 전환)
                  </Button>
                )}
                <Button variant="outline" onClick={onClose}>
                  닫기
                </Button>
              </Flex>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* 학업시간표 확인 모달 */}
      <Modal isOpen={isScheduleModalOpen} onClose={onScheduleModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedApplicant?.profile?.name}님의 학업시간표
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            {selectedApplicant?.academicSchedule ? (
              <Stack spacing={4}>
                <Box>
                  <Text fontWeight="bold">학기: {selectedApplicant.academicSchedule.semesterName}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {selectedApplicant.academicSchedule.startDate} ~ {selectedApplicant.academicSchedule.endDate}
                  </Text>
                </Box>

                <Divider />

                <Text fontWeight="bold">
                  근무 예정일: {getDayNameKorean(getDayOfWeek(schedule.workDate))}요일
                </Text>

                <Box>
                  <Text fontWeight="bold" mb={2}>해당 요일 수업:</Text>
                  {(() => {
                    const dayName = getDayOfWeek(schedule.workDate);
                    const classes = selectedApplicant.academicSchedule.weeklySchedule?.[dayName] || [];
                    
                    if (classes.length === 0) {
                      return <Text color="gray.500">수업 없음</Text>;
                    }

                    return (
                      <Stack spacing={2}>
                        {classes.map((cls, idx) => (
                          <Box key={idx} p={2} borderWidth="1px" borderRadius="md">
                            <Text fontWeight="bold">{cls.subject}</Text>
                            <Text fontSize="sm">{cls.start} - {cls.end}</Text>
                          </Box>
                        ))}
                      </Stack>
                    );
                  })()}
                </Box>

                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontWeight="bold" color="blue.700">근무시간</Text>
                  <Text>{schedule.startTime} - {schedule.endTime}</Text>
                </Box>
              </Stack>
            ) : (
              <Text color="gray.500">등록된 학업시간표가 없습니다.</Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onScheduleModalClose}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 근무자 할당 모달 */}
      <WorkerAssignmentModal
        isOpen={isWorkerAssignOpen}
        onClose={onWorkerAssignClose}
        schedule={schedule}
        onAssigned={() => {
          loadApplications();
          onUpdate();
        }}
      />
    </>
  );
}

export default ApplicationList;
